import React from 'react';

import useEvent from '@lyearn/core/hooks/useEvent';
import { Trans, useTranslation } from '@lyearn/i18n';
import { cn } from '@lyearn/style';
import { Input, Stack, Textarea, Typography } from '@lyearn/ui';
import { Dialog } from '@/components/Modals';
import { EventUnion, UpdateEventStatusTypeEnum } from '@/types/schema';

import { getIsRecurringSession, getIsRecurringSessionInstance } from '../../../../utils';
import { DialogActions } from '../../types';
import ListItemContent from '../ListItemContainer/ListItemContent';
import { dialogStyles, textAreaInputStyles } from './styles';
import UpdateEventStatusType from './UpdateEventStatusType';

const dialogClasses = (isRecurringInstance: boolean) => ({
  paper: dialogStyles(isRecurringInstance ? 52 : 41.6),
});

export type ArchiveSessionDialogProps = {
  Image: JSX.Element;
  onAction: (props: {
    dialogAction: DialogActions;
    message?: string;
    updateType?: UpdateEventStatusTypeEnum;
  }) => void;
  event: EventUnion;
};
// the dialog has to be self sufficient
function ArchiveSessionDialog({ Image, onAction, event }: ArchiveSessionDialogProps) {
  const { t } = useTranslation();
  const [cancellationMsg, setCancellationMsg] = React.useState<string>();
  const isRecurringInstance = getIsRecurringSessionInstance(event.recurringCategory);
  const isRecurring = getIsRecurringSession(event.recurringCategory);

  const [updateType, setUpdateType] = React.useState<UpdateEventStatusTypeEnum>(
    UpdateEventStatusTypeEnum.Current,
  );

  const onInputBlur = useEvent(
    (changeEvent: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const text = changeEvent.currentTarget.value;
      setCancellationMsg(text || undefined);
    },
  );

  const onSubmit = React.useCallback(() => {
    onAction({
      dialogAction: DialogActions.Archive,
      message: cancellationMsg,
      updateType: isRecurringInstance ? updateType : undefined,
    });
  }, [cancellationMsg, isRecurringInstance, onAction, updateType]);

  return (
    <Dialog
      action={{
        appearance: 'danger',
        text: t('Common:Actions.Cancel'),
        discardText: t('Events:Manager.KeepActive'),
        onSubmit,
        className: '!mt-20 !pt-16 !pb-16',
      }}
      classes={dialogClasses(isRecurringInstance)}
      header={{
        title: isRecurringInstance
          ? t('Events:RecurringSession.CancelRecurringSession')
          : t('Events:Manager.CancelSessionTitle'),
        description: isRecurring
          ? t('Events:RecurringSession.CancelRecurringDescription')
          : t('Events:Manager.CancelSessionDescription'),
        className: 'pt-28',
        classes: {
          description: 'pe-8',
        },
      }}
      onClose={() => onAction({ dialogAction: DialogActions.Close })}>
      <Stack className="px-24" direction="col" gapY={isRecurringInstance ? 24 : 8}>
        <ListItemContent
          Image={Image}
          className="rounded-8 border border-interactive-secondary p-12"
          event={event}
          showRecurringIcon={false}
        />
        {isRecurringInstance ? (
          <Stack direction="col">
            <UpdateEventStatusType updateType={updateType} onChange={setUpdateType} />
            <Typography className="mb-8 ms-8" variant="body-short-02">
              <Trans
                color="text-text-placeholder"
                i18nKey="Events:Manager.CancelationMessage"
                variant="body-short-02"
              />
            </Typography>
            <Textarea
              className={cn('h-[10.6rem] w-full', textAreaInputStyles)}
              maxRows={3}
              minRows={isRecurringInstance ? 3 : undefined}
              placeholder={t('Events:Manager.RecurringInstanceCancellationMessagePlaceholder')}
              onBlur={onInputBlur}
            />
          </Stack>
        ) : (
          <Input
            className="w-full"
            placeholder={t('Events:Manager.CancellationMessagePlaceholder')}
            onBlur={onInputBlur}
          />
        )}
      </Stack>
    </Dialog>
  );
}

export default React.memo(ArchiveSessionDialog);
